import type { CallRecommendation } from "../../../services/grantsApi";

/**
 * Moderator-facing messages for the single-recommendation review flow
 * (approve / reject / revoke). Shared by GrantsRecommendationDetails (page) and
 * RecommendationDetailsModal so the two copies cannot drift apart again.
 */

/**
 * Error message for a review mutation that exceeded its timeout
 */
export const TIMEOUT_ERROR_MESSAGE = "Przekroczono czas oczekiwania";

/**
 * Stable fragments of the ConflictException texts thrown by
 * CallRecommendationService.executeCreateNew() on a callNumber collision:
 * AC-3 (same callNumber, disjoint date windows) and AC-6 (race on a
 * moderator-forced create_new). Keep in sync with
 * apps/backend/src/grants/calls/services/call-recommendation.service.ts.
 */
const CALL_NUMBER_COLLISION_FRAGMENTS = [
  "jest już użyty przez inny nabór",
  "został właśnie użyty przez inny nabór",
];

/**
 * Maps a recognized backend error message to a moderator-facing Polish text.
 * Returns null when the message is not one of the known review errors.
 */
const mapKnownRecommendationMessage = (originalMessage: string): string | null => {
  const normalizedMessage = originalMessage.toLowerCase();

  if (normalizedMessage.includes("already reviewed")) {
    return "Rekomendacja już została przejrzana przez innego użytkownika.";
  }
  if (normalizedMessage.includes("not found")) {
    return "Rekomendacja nie została znaleziona.";
  }
  if (normalizedMessage.includes("cofnąć tylko odrzuconą")) {
    return "Można cofnąć tylko odrzuconą rekomendację";
  }
  if (normalizedMessage.includes("forcedaction wymaga podania overrides.callnumber")) {
    return "Aby utworzyć nowy nabór, podaj numer naboru różny od kolidującego.";
  }
  // AC-3/AC-6: the backend text is already actionable Polish (names the
  // colliding callNumber and says what to do), so it is shown verbatim.
  if (CALL_NUMBER_COLLISION_FRAGMENTS.some((fragment) => normalizedMessage.includes(fragment))) {
    return originalMessage;
  }
  return null;
};

/**
 * Maps API error messages to user-friendly Polish messages
 */
export const getRecommendationErrorMessage = (error: unknown): string => {
  // Check for timeout error
  if (error instanceof Error && error.name === "TimeoutError") {
    return TIMEOUT_ERROR_MESSAGE;
  }

  const errorObj = error as { response?: { status?: number; data?: { message?: unknown } }; message?: unknown };
  const rawMessage = errorObj.response?.data?.message || errorObj.message;

  // Normalize message to string (NestJS validation errors return array)
  let originalMessage: string;
  if (Array.isArray(rawMessage)) {
    originalMessage = rawMessage[0] || "";
  } else if (typeof rawMessage === "string") {
    originalMessage = rawMessage;
  } else if (rawMessage && typeof rawMessage === "object") {
    originalMessage = (rawMessage as { message?: string }).message || JSON.stringify(rawMessage);
  } else {
    originalMessage = "";
  }
  // Known messages (incl. AC-3/AC-6 collisions) win over the generic 409 fallback
  const knownMessage = mapKnownRecommendationMessage(originalMessage);
  if (knownMessage) {
    return knownMessage;
  }

  if (errorObj.response?.status === 409) {
    return "Rekomendacja już została przejrzana.";
  }
  if (errorObj.response?.status === 404) {
    return "Rekomendacja nie istnieje.";
  }
  if (errorObj.response?.status && errorObj.response.status >= 500) {
    return "Błąd serwera. Spróbuj ponownie później.";
  }
  if (!errorObj.response) {
    return "Błąd połączenia. Sprawdź połączenie z internetem.";
  }

  return "Wystąpił nieoczekiwany błąd. Spróbuj ponownie.";
};

/**
 * Moderator-facing text for one failed item of a bulk approve/reject
 * (`BulkOperationResult.errors[].error`, the backend exception message).
 * Bulk items carry no HTTP status, so unrecognized messages are shown as-is.
 */
export const describeBulkItemError = (message: string): string =>
  mapKnownRecommendationMessage(message) ?? (message.trim() || "Nieznany błąd.");

export interface ApproveToast {
  message: string;
  type: "success" | "warning";
}

/**
 * AC-4: picks the approve-success toast. create_new can be silently redirected
 * to update_existing when the callNumber collides with an existing call, so
 * that outcome gets a warning instead of the same toast regardless of outcome.
 *
 * Intent is derived from the request inputs (the recommendation's
 * suggestedAction, or an AC-5 forcedAction actually sent with the approve
 * call), not from the response's `action`; `isNewCall` tells what was written.
 */
export const deriveApproveToast = ({
  suggestedAction,
  forcedAction,
  isNewCall,
}: {
  suggestedAction: CallRecommendation["suggestedAction"] | undefined;
  forcedAction?: "create_new";
  isNewCall: boolean;
}): ApproveToast => {
  const intendedCreateNew = suggestedAction === "create_new" || forcedAction === "create_new";
  if (intendedCreateNew && !isNewCall) {
    return {
      message:
        "Rekomendacja zatwierdzona — ale zapisana jako aktualizacja istniejącego naboru (kolizja numeru naboru)",
      type: "warning",
    };
  }
  if (intendedCreateNew) {
    return { message: "Rekomendacja zatwierdzona — utworzono nowy nabór", type: "success" };
  }
  return { message: "Rekomendacja zatwierdzona", type: "success" };
};
